<template>
  <v-card>
    <v-card-title class="primary white--text headline">
      <span>{{ $t('completeContractInformation')}}</span>
      <v-spacer/>
      <BaseDialogActions hideRefresh/>
    </v-card-title>
    <v-card-text>
      <v-container fluid>
        <FormWrapper
          :buttonText="$t('confirm')"
          @submit="confirmButtonClicked"
          :disabled="!enableSubmit"
        >
          <v-row dense align="center">
            <v-col cols="12">
              <DataTable
                :items.sync="contractsMapped"
                :headers="columnSet"
                :customCells="customColumns"
                class="pt-2"
              >
                <template #contract="{item}">
                  <v-autocomplete
                    v-model="item.contract"
                    :label="$t('contract')"
                    :items="item.contracts"
                    item-value="contractId"
                    :readonly="item.contractId !== undefined && item.contractId !== null"
                    color="black"
                    return-object
                    style="width: 25vw;"
                    @change="handleContractChange(item)"
                  >
                    <template #item="{item}">
                      <v-container>
                        <v-row dense no-gutters>
                          <v-col cols="6" v-for="(field, i) in contractFields(item)" :key="i">
                            <Icon
                              :icon="field.icon"
                              :tooltipText="$t(field.name)"
                              iconColor="grey"
                              propagateClick
                            />
                            <span>
                              {{item[field.name] ?? 'N/A'}}
                            </span>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-divider/>
                        </v-row>
                      </v-container>
                    </template>
                    <template #selection="{item}">
                      <div class="mr-2" v-for="(field, i) in contractFields(item)" :key="i">
                        <v-icon small class="mb-1 mr-1">{{field.icon}}</v-icon>
                        <span>{{item[field.name] ?? 'N/A'}}</span>
                      </div>
                    </template>
                  </v-autocomplete>
                </template>
                <template #product="{item}">
                  <v-autocomplete
                    v-if="item.contract"
                    v-model="item.product"
                    :label="$t('product')"
                    :items="item.contract.products"
                    item-value="productId"
                    color="black"
                    style="width: 15vw;"
                    return-object
                  >
                    <template #item="{item}">
                      <div class="mr-2">
                        <span>{{item.name ?? 'N/A'}}</span>
                      </div>
                    </template>
                    <template #selection="{item}">
                      <v-row dense align="center">
                        <v-col cols="auto" class="mr-2">
                          <span>{{item.name ?? 'N/A'}}</span>
                        </v-col>
                        <v-col>
                          <Icon
                            v-if="item.isScalable"
                            icon="mdi-ruler-square"
                            :tooltipText="$t('scalable')"
                            iconColor="green"
                            :small="false"
                          />
                          <Icon
                            v-else
                            icon="mdi-weight"
                            :tooltipText="$t('weightBased')"
                            iconColor="black"
                            :small="false"
                          />
                        </v-col>
                      </v-row>
                    </template>
                  </v-autocomplete>
                  <v-col v-else class="black--text" style="width: 15vw;">{{ $t('selectContract') }} </v-col>
                </template>
                <template #actions="{ item }">
                  <Icon
                    v-if="item.ticketType?.value === ScaleSheetTicketType.IncludesVolume.value || item.hasVolumeData"
                    icon="mdi-ruler-square"
                    :tooltipText="$t('viewScaleData')"
                    @icon-clicked="showScaleData(item)"
                    :small="false"
                    :iconColor="item.reviewed ? 'grey darken-2' : 'primary'"
                  />
                  <Icon
                    v-else-if="item.ticketType?.value === ScaleSheetTicketType.WeightBased.value"
                    icon="mdi-weight"
                    :tooltipText="$t('weightBased')"
                    :small="false"
                    iconColor="grey darken-2"
                  />
                  <Icon
                    v-else-if="item.ticketType?.value === ScaleSheetTicketType.Expanded.value"
                    icon="mdi-information-outline"
                    :tooltipText="$t('expandedTicketNoScaleData')"
                    :small="false"
                    iconColor="grey darken-2"
                  />
                </template>
              </DataTable>
            </v-col>
          </v-row>
        </FormWrapper>
      </v-container>
    </v-card-text>
    <Dialog :stateId="dialogId" @close="resetDialogs">
      <ReviewVolumeDataDialog
        v-if="reviewVolumeDataDialog"
        :propVolumeData="parsedScaleData"
        :propSpecies="allSpecies"
        :propGrades="allGrades"
        :propExtTicketNumber2="Number(selectedTicketNumber)"
        @close-dialog="resetDialogs"
        @reviewed="resetDialogs"
      />
    </Dialog>
  </v-card>
</template>

<script>
import TicketHeaders from '@/headers/Ticket'
import { cloneDeep } from 'lodash'
import { ContractType, ScaleSheetTicketType } from '@/utils/Enumerations.js'
import { mapActions, mapGetters } from 'vuex'
export default {
  name: 'SelectContractForScaleDataTicketDialog',
  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    ReviewVolumeDataDialog: () => import('@/components/ticket/ticket-details/scale-data/ReviewVolumeDataDialog.vue'),
    Dialog: () => import('@/components/Dialog.vue')
  },

  props: {
    propContracts: {
      type: Array,
      required: true
    }
  },

  data: () => ({
    columnSet: undefined,
    dialogId: 'select-contract-for-scale-data-dialog',
    reviewVolumeDataDialog: false,
    parsedScaleData: {},
    contractsMapped: [],
    selectedTicketNumber: undefined,
    ScaleSheetTicketType
  }),

  async created () {
    this.columnSet = this.selectContractDialogHeaders.getColumns()
    this.initializeContracts()
  },

  computed: {
    ...mapGetters('species', ['allSpecies']),
    ...mapGetters('grade', ['allGrades']),
    customColumns () {
      return [
        {
          slotName: 'contract',
          value: 'contract'
        },
        {
          slotName: 'product',
          value: 'product'
        },
        {
          slotName: 'actions',
          value: 'actions'
        }
      ]
    },
    selectContractDialogHeaders () {
      return TicketHeaders.selectContractForScaleDataDialogHeaders()
    },
    enableSubmit () {
      return this.contractsMapped.every(c => c.contract !== undefined && c.contract !== null && c.product !== undefined && c.product !== null)
    }
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    initializeContracts () {
      this.contractsMapped = cloneDeep(this.propContracts).map(c => {
        return {
          ...c,
          contract: c.contracts.find(ct => ct.contractId === c.contractId) || undefined,
          product: c.contracts.find(ct => ct.contractId === c.contractId)?.products.find(p => p.productId === c.productId) || undefined,
          hasVolumeData: c.volumeData !== undefined && c.volumeData !== null && c.volumeData?.segmentVolumes?.length > 0
        }
      })
    },
    handleContractChange (item) {
      item.product = item.contract.products.length === 1
        ? item.contract.products[0]
        : item.contract.products.length === 2
          ? item.contract.products.find(p => !p.isFallback)
          : undefined
    },
    confirmButtonClicked () {
      this.$emit('contracts-selected', this.contractsMapped)
    },
    contractFields (contract) {
      return contract.type === ContractType.LogYardSale.value ? [
        { name: 'fromAccount', icon: 'mdi-account-arrow-right' },
        { name: 'destination', icon: 'mdi-domain' },
        { name: 'account', icon: 'mdi-account' }
      ] : [
        { name: 'tract', icon: 'mdi-crosshairs-gps' },
        { name: 'destination', icon: 'mdi-domain' },
        { name: 'setting', icon: 'mdi-map-marker-circle' },
        { name: 'account', icon: 'mdi-account' }
      ]
    },
    resetDialogs () {
      this.reviewVolumeDataDialog = false
      this.closeDialogsAtOrAbove(this.dialogId)
    },
    showScaleData (item) {
      this.parsedScaleData = item.volumeData
      this.selectedTicketNumber = item.extTicketNumber2
      this.reviewVolumeDataDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
    }
  }
}
</script>
